// AXIS ZERO - Game Loop Module
// Main game loop, state management, and coordination with all systems
// High Mode: streak >= 5, red colors, +3% speed

import { CONFIG } from './Config';
import { Player } from './Player';
import { ObstacleManager } from './Obstacles';
import { LevelGenerator } from './LevelGenerator';
import { EffectsManager } from './Effects';
import { SkinManager } from './SkinManager';
import { LeaderboardService } from './LeaderboardService';
import { StreakSystem } from './StreakSystem';
import { audioManager } from './AudioManager';

export const GAME_STATES = {
  READY: 'ready',
  PLAYING: 'playing',
  DYING: 'dying',
  GAME_OVER: 'gameOver',
};

export const GAME_MODES = {
  NORMAL: 'normal',
  PRACTICE: 'practice',
};

export class GameLoop {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
      // Disable image smoothing to reduce shimmer on moving 1px lines
      this.ctx.imageSmoothingEnabled = false;
    
    // Initialize modules
    this.player = new Player(canvas.height);
    this.obstacleManager = new ObstacleManager();
    this.levelGenerator = new LevelGenerator();
    this.effectsManager = new EffectsManager();
    this.skinManager = new SkinManager();
    this.leaderboardService = new LeaderboardService();
    this.streakSystem = new StreakSystem();
    
    // Game state
    this.state = GAME_STATES.READY;
    this.mode = GAME_MODES.NORMAL;
    this.score = 0;
    this.highScore = this.leaderboardService.getHighScore();
    this.isNewHighScore = false;
    this.runId = null;
    
    // Timing
    this.lastTime = 0;
    this.animationFrameId = null;
    this.isRunning = false;
    
    // Score update throttling
    this.lastScoreCallbackTime = 0;
    this.scoreCallbackThrottle = 100;
    
    // Floor grid animation
    this.gridOffset = 0;
    
    // Background streaks (subtle vertical falling lines)
    this.bgStreaks = [];
    this.initBgStreaks();
    
    // Dust particles near ground
    this.dustParticles = [];
    
    // Callbacks
    this.onGameOver = null;
    this.onScoreUpdate = null;
    this.onNearMiss = null;
  }

  initBgStreaks() {
    this.bgStreaks = [];
    for (let i = 0; i < CONFIG.BG_STREAK_COUNT; i++) {
      this.bgStreaks.push({
        x: Math.random(),
        y: Math.random(),
        speed: 0.15 + Math.random() * 0.35,
        length: 0.02 + Math.random() * 0.06,
      });
    }
  }

  start(mode = GAME_MODES.NORMAL) {
    // Guard against double-start
    if (this.isRunning || this.state === GAME_STATES.PLAYING) {
      return;
    }
    
    this.mode = mode;
    this.reset();
    this.state = GAME_STATES.PLAYING;
    this.isRunning = true;
    this.runId = Math.random().toString(36).substring(2, 15);
    this.lastTime = performance.now();
    this.lastScoreCallbackTime = 0;
    
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    
    this.loop(this.lastTime);
    audioManager.startMusic();
  }

  reset() {
    this.player.reset(this.canvas.height);
    this.obstacleManager.reset();
    this.levelGenerator.reset();
    this.effectsManager.reset();
    this.streakSystem.reset();
    this.score = 0;
    this.isNewHighScore = false;
    this.gridOffset = 0;
    this.dustParticles = [];
    this.state = GAME_STATES.READY;
    
    audioManager.restoreVolume();
  }

  stop() {
    this.isRunning = false;
    
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  jump() {
    if (this.state === GAME_STATES.PLAYING) {
      const jumped = this.player.jump();
        if (jumped) {
          // Jump buff from High Mode
          if (this.streakSystem && this.streakSystem.jumpBuffTimer > 0) {
            this.player.velocityY *= 1.20;
          }
          audioManager.playJump();
        }
    }
  }

  loop = (currentTime) => {
    if (!this.isRunning) {
      return;
    }
    
    const deltaTime = Math.min((currentTime - this.lastTime) / 1000, 0.1);
    this.lastTime = currentTime;
    
    if (this.state === GAME_STATES.PLAYING || this.state === GAME_STATES.DYING) {
      this.update(deltaTime, currentTime);
    }
    
    this.render();
    
    if (this.isRunning) {
      this.animationFrameId = requestAnimationFrame(this.loop);
    }
  }

  update(deltaTime, currentTime) {
    const timeScale = this.effectsManager.getTimeScale();
    const isPracticeMode = this.mode === GAME_MODES.PRACTICE;
    
    // Update level generator
    this.levelGenerator.update(deltaTime * timeScale);
    
    // Get current speed with High Mode multiplier applied as a layer
    const baseSpeed = this.levelGenerator.getCurrentSpeed(isPracticeMode);
    const highModeMult = this.streakSystem.getSpeedMultiplier();
    const currentSpeed = baseSpeed * highModeMult;
    const speedMult = this.levelGenerator.getSpeedMultiplier() * highModeMult;
    
    // Update effects trail speed
    this.effectsManager.setTrailSpeedMultiplier(speedMult);
    
    // Update player with streak glow
    this.player.setGlowMultiplier(this.streakSystem.getGlowMultiplier());
    this.player.update(deltaTime, timeScale);
    
    // Update streak system
    this.streakSystem.update(deltaTime, this.levelGenerator.getGameTime());
    
    // Update obstacles
    this.obstacleManager.update(
      deltaTime,
      currentSpeed,
      this.canvas.width,
      this.player.groundY,
      timeScale
    );
    
    // Spawn new obstacles as needed
    while (this.obstacleManager.shouldSpawnMore(this.canvas.width)) {
      const pattern = this.levelGenerator.getNextPattern();
      this.obstacleManager.spawnObstacle(
        this.canvas.width, 
        pattern, 
        currentSpeed,
        this.levelGenerator.getGameTime()
      );
    }
    
    // Update effects
    this.effectsManager.update(
      deltaTime,
      this.player.x,
      this.player.y,
      this.player.isGrounded,
      this.streakSystem.getGlowMultiplier()
    );
    
    // Update grid offset for floor animation
    this.gridOffset = (this.gridOffset + currentSpeed * deltaTime * timeScale) % 50;
    
    // Update background streaks
    const speedFactor = Math.min(currentSpeed / CONFIG.MAX_SPEED, 1);
    this.bgStreaks.forEach(s => {
      s.y += s.speed * deltaTime * timeScale * (0.3 + speedFactor * 0.7);
      if (s.y > 1 + s.length) {
        s.y = -s.length;
        s.x = Math.random();
      }
    });
    
    // Update dust particles near ground
    if (this.state === GAME_STATES.PLAYING) {
      if (this.dustParticles.length < 8 && Math.random() < 0.06 * speedFactor) {
        this.dustParticles.push({
          x: this.canvas.width + Math.random() * 20,
          y: this.player.groundY - Math.random() * 12,
          vx: -(currentSpeed * 0.12 + Math.random() * 10),
          vy: -(Math.random() * 6),
          life: 0.4 + Math.random() * 0.4,
          maxLife: 0.8,
          size: 1 + Math.random() * 1.5,
        });
      }
    }
    this.dustParticles = this.dustParticles.filter(p => {
      p.x += p.vx * deltaTime * timeScale;
      p.y += p.vy * deltaTime * timeScale;
      p.life -= deltaTime;
      return p.life > 0 && p.x > -10;
    });
    
    // Check collisions (only when playing)
    if (this.state === GAME_STATES.PLAYING) {
      const playerBounds = this.player.getBounds();
      const collision = this.obstacleManager.checkCollision(playerBounds, this.player.groundY);
      
      if (collision.collided) {
        if (isPracticeMode) {
          this.triggerPracticeHit(collision.obstacle);
        } else {
          this.triggerDeath(collision.obstacle);
          return;
        }
      }
      
      // Check near-miss
      const nearMiss = this.obstacleManager.checkNearMiss(playerBounds, this.player.groundY);
      if (nearMiss.isNearMiss) {
        this.handleNearMiss(nearMiss.obstacleId);
      }
      
      // Update internal score (always)
      this.score = this.levelGenerator.getGameTime() + this.streakSystem.getBonusPoints();
      
      // Throttled score callback - only call every 100ms
      if (this.onScoreUpdate && currentTime - this.lastScoreCallbackTime >= this.scoreCallbackThrottle) {
        this.lastScoreCallbackTime = currentTime;
        this.onScoreUpdate(this.score);
      }
    }
    
    // Check if death sequence is complete
    if (this.state === GAME_STATES.DYING && this.effectsManager.isDeathSequenceComplete()) {
      this.completeGameOver();
    }
  }

  handleNearMiss(obstacleId) {
    this.effectsManager.triggerNearMiss();
    this.player.triggerNearMissBlink();
    audioManager.playNearMiss();
    
    // Get world colors for spark color
    const worldColors = this.streakSystem.getWorldColors();
    
    // Spawn sparks at obstacle position
    const lastObs = this.obstacleManager.getLastPassedObstacle();
    if (lastObs) {
      const sparkX = lastObs.x + lastObs.width;
      const sparkY = this.player.groundY - lastObs.height / 2;
      this.effectsManager.spawnNearMissSparks(sparkX, sparkY, worldColors.NEAR_MISS_SPARK);
    }
    
    // Pass obstacle ID for deduplication
    const streakResult = this.streakSystem.registerNearMiss(this.levelGenerator.getGameTime(), obstacleId);
    
    // Check for entering high mode
    if (streakResult && streakResult.enteredHighMode) {
      this.effectsManager.setHighStreakPulse(true);
    }
    
    // Only trigger callback if streak was actually incremented
    if (this.onNearMiss && streakResult) {
      this.onNearMiss(streakResult);
    }
  }

  triggerPracticeHit(obstacle) {
    this.effectsManager.triggerScreenShake(CONFIG.SCREEN_SHAKE_INTENSITY * 0.5, 0.15);
    this.player.deathFlashAlpha = 0.3;
    audioManager.playDeath();
  }

  triggerDeath(obstacle) {
    this.state = GAME_STATES.DYING;
    
    // Immediately reset streak on death
    this.streakSystem.onDeath();
    
    this.player.triggerDeath();
    this.effectsManager.triggerDeathSequence();
    audioManager.setDeathVolume();
    audioManager.playDeath();
  }

  async completeGameOver() {
    this.state = GAME_STATES.GAME_OVER;
    this.isRunning = false;
    
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    
    // Check for new high score (only in normal mode)
    if (this.mode === GAME_MODES.NORMAL) {
      this.isNewHighScore = this.leaderboardService.isNewHighScore(this.score);
      if (this.isNewHighScore) {
        this.leaderboardService.addScore(this.score);
        this.highScore = this.score;
      }
    }
    
    if (this.onGameOver) {
      this.onGameOver({
        score: this.score,
        isNewHighScore: this.isNewHighScore,
        highScore: this.highScore,
        maxStreak: this.streakSystem.maxStreak,
        isPracticeMode: this.mode === GAME_MODES.PRACTICE,
      });
    }
  }

  render() {
    const ctx = this.ctx;
    const width = this.canvas.width;
    const height = this.canvas.height;
    
    // Get world colors (switches to red in high mode)
    const worldColors = this.streakSystem.getWorldColors();
    const isHighMode = this.streakSystem.isInHighMode();
    
    // Apply screen shake
    const shake = this.effectsManager.getShakeOffset();
    ctx.save();
    ctx.translate(Math.round(shake.x), Math.round(shake.y));
    
    // Clear canvas - pure black
    ctx.fillStyle = CONFIG.COLORS.BACKGROUND;
    ctx.fillRect(-10, -10, width + 20, height + 20);
    
    // Background streaks (subtle vertical falling lines)
    this.drawBackgroundStreaks(ctx, width, height, isHighMode);
    
    // Ground haze (above ground line)
    this.drawGroundHaze(ctx, width, worldColors);
    
    // Draw ground line
    this.drawGround(ctx, width, height, worldColors, isHighMode);
      // Parallax floor grid (subtle)
      this.drawParallaxGrid(ctx, width, height, this.player.groundY, worldColors);
    
    // Draw dust particles near ground
    this.drawDustParticles(ctx, worldColors);
    
    // Draw obstacles
    this.obstacleManager.draw(ctx, this.player.groundY, worldColors);
    
    // Draw player
    this.player.draw(ctx, worldColors);
    
    // Draw effects (sparks, etc)
    this.effectsManager.draw(ctx, width, height, this.player.groundY, worldColors);
    
    // Draw HIGH MODE entry text on canvas
    this.drawHighModeText(ctx, width, worldColors);
    
    ctx.restore();
  }

  drawBackgroundStreaks(ctx, width, height, isHighMode) {
    const speedFactor = this.levelGenerator.getSpeedMultiplier();
    
    let baseAlpha = CONFIG.BG_STREAK_BASE_ALPHA + (speedFactor - 1) * 0.025;
    if (isHighMode) baseAlpha *= 1.3;
    baseAlpha = Math.min(baseAlpha, CONFIG.BG_STREAK_MAX_ALPHA);
    
    const streakColor = isHighMode
      ? CONFIG.COLORS_HIGH_MODE.BG_STREAK
      : CONFIG.COLORS.BG_STREAK;
    
    ctx.save();
    ctx.strokeStyle = streakColor;
    ctx.lineWidth = 1;
    
    this.bgStreaks.forEach(s => {
      const sx = s.x * width;
      const sy = s.y * height;
      const sLen = s.length * height;
      const alpha = baseAlpha * (0.4 + s.speed * 0.8);
      
      if (alpha < 0.005) return;
      
      ctx.globalAlpha = alpha;
      ctx.beginPath();
      ctx.moveTo(sx, sy);
      ctx.lineTo(sx, sy + sLen);
      ctx.stroke();
    });
    
    ctx.restore();
  }

  drawGroundHaze(ctx, width, worldColors) {
    const groundY = this.player.groundY;
    const hazeHeight = CONFIG.GROUND_HAZE_HEIGHT;
    const hazeColor = worldColors.FLOOR_HAZE || CONFIG.COLORS.FLOOR_HAZE;
    
    ctx.save();
    const gradient = ctx.createLinearGradient(0, groundY - hazeHeight, 0, groundY);
    gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
    gradient.addColorStop(1, hazeColor);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, groundY - hazeHeight, width, hazeHeight);
    ctx.restore();
  }

  drawGround(ctx, width, height, worldColors, isHighMode) {
    const groundY = this.player.groundY;
    
    ctx.save();
    
    // Ground line - slightly thicker in High Mode
    ctx.strokeStyle = worldColors.FLOOR_LINE;
    ctx.lineWidth = isHighMode ? 5 : 4;
    ctx.shadowColor = worldColors.FLOOR_GLOW;
    ctx.shadowBlur = isHighMode ? 30 : 25;
    
    ctx.beginPath();
    ctx.moveTo(0, groundY);
    ctx.lineTo(width, groundY);
    ctx.stroke();
    
    // Second pass for bloom effect
    ctx.shadowBlur = isHighMode ? 60 : 50;
    ctx.globalAlpha = 0.4;
    ctx.stroke();
    
    ctx.restore();
  }

  drawDustParticles(ctx, worldColors) {
    if (this.dustParticles.length === 0) return;
    
    ctx.save();
    const color = worldColors.PARTICLE || CONFIG.COLORS.PARTICLE;
    ctx.fillStyle = color;
    
    this.dustParticles.forEach(p => {
      ctx.globalAlpha = (p.life / p.maxLife) * 0.35;
      ctx.fillRect(p.x, p.y, p.size, p.size);
    });
    
    ctx.restore();
  }

  drawHighModeText(ctx, width, worldColors) {
    const info = this.streakSystem.getDisplayInfo();
    if (!info.showHighModeText || info.highModeTextAlpha <= 0) return;
    
    ctx.save();
    ctx.globalAlpha = info.highModeTextAlpha;
    ctx.textAlign = 'center';
    ctx.font = '600 13px "Orbitron", sans-serif';
    ctx.fillStyle = worldColors.SCORE_TEXT || CONFIG.COLORS.SCORE_TEXT;
    ctx.shadowColor = worldColors.SCORE_TEXT || CONFIG.COLORS.SCORE_TEXT;
    ctx.shadowBlur = 8;
    ctx.fillText('HIGH MODE', width / 2, 90);
    ctx.restore();
  }

  drawVignette(ctx, width, height) {
    // Disabled for minimal look
  }


  // Subtle parallax perspective grid above the floor
  drawParallaxGrid(ctx, width, height, groundY, worldColors) {
    // Keep it cheap + stable (no glow, low alpha)
    ctx.save();
    ctx.shadowBlur = 0;
    ctx.lineWidth = 1;
    ctx.strokeStyle = worldColors.FLOOR_LINE || "rgba(255,255,255,0.25)";

    const planeTop = groundY + 6;
    const planeBottom = height;
    const planeH = Math.max(1, planeBottom - planeTop);

    // Perspective curve (closer lines near bottom)
    const perspectiveY = (t) => planeTop + planeH * Math.pow(t, 1.65);

    // Horizontal lines
    const step = 0.12; // fewer lines
    const zStep = 60;
    const offset = ((this.gridOffset || 0) % zStep) / zStep * step;

    for (let t = offset; t <= 1.0; t += step) {
      const y = Math.round(perspectiveY(t)) + 0.5;
      ctx.globalAlpha = 0.07 * (1 - t);
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }

    // Vertical perspective lines
    const vpX = width * 0.52;
    const columns = 7;
    for (let i = -columns; i <= columns; i++) {
      const xBottom = vpX + i * (width * 0.13);
      const xTop = vpX + i * (width * 0.02);
      ctx.globalAlpha = 0.055;
      ctx.beginPath();
      ctx.moveTo(Math.round(xTop) + 0.5, planeTop);
      ctx.lineTo(Math.round(xBottom) + 0.5, planeBottom);
      ctx.stroke();
    }

    ctx.restore();
  }

  handleResize(width, height) {
    this.canvas.width = width;
    this.canvas.height = height;
    this.player.updateCanvasHeight(height);
  }

  getState() {
    return this.state;
  }

  getMode() {
    return this.mode;
  }

  getScore() {
    return this.score;
  }

  getHighScore() {
    return this.highScore;
  }
}

export default GameLoop;
